/**
 * Canvas Config Adapter
 *
 * Converts UI product data to CanvasConfig for the CanvasEditor component.
 * This bridges the gap between product-transformer types and canvas types.
 */

import type { CanvasConfig } from '@/types/canvas';
import type {
  UIProductMockupTemplate,
  UIProductColor,
} from '@/lib/product-transformer';

/**
 * Create a CanvasConfig from a mockup template and color.
 *
 * @param template - The active mockup template
 * @param color - The selected product color (optional)
 * @returns CanvasConfig for the CanvasEditor
 */
export function createCanvasConfig(
  template: UIProductMockupTemplate,
  color: UIProductColor | null,
  _hsCode?: string | null
): CanvasConfig {
  return {
    // Mockup dimensions (= Canvas native size)
    mockupWidth: template.mockupWidthPx,
    mockupHeight: template.mockupHeightPx,

    // Print area within the mockup: this template's rectangle (templateRect)
    printArea: { ...template.printAreaRect },

    // Layer URLs + stack order (the mockup image is the shading layer).
    // The editor shows exactly one mockup large, so it takes the high-res
    // rendering assets when present; the coordinate system stays the
    // creator size (mockupWidth/Height) and the image is scaled to fit.
    mockupImageUrl: template.renderingImageUrl ?? template.imageUrl,
    baseOpacity: template.baseOpacity,
    layerOrder: template.layerOrder,
    backgroundColor: color?.hex || '#FFFFFF',

    // Overlay URLs (optional)
    heatheredOverlayUrl: template.heatheredOverlayUrl,
    textureOverlayUrl: template.textureOverlayUrl,
    shadowOverlayUrl: template.shadowOverlayUrl,
    topOverlayUrl: template.topOverlayUrl,
    cutoutMaskUrl: template.renderingCutoutMaskUrl ?? template.cutoutMaskUrl,
    toplayerMaskUrl: template.toplayerMaskUrl,

    // Overlay settings
    heatheredBlendMode: template.heatheredBlendMode,
    heatheredOpacity: template.heatheredOpacity,
    textureBlendMode: template.textureBlendMode,
    textureOpacity: template.textureOpacity,
    shadowBlendMode: template.shadowBlendMode,
    shadowOpacity: template.shadowOpacity,

    // Flags
    isHeathered: color?.isHeathered ?? false,
  };
}

/**
 * Create a minimal CanvasConfig for testing or fallback.
 *
 * @param width - Canvas width
 * @param height - Canvas height
 * @param backgroundColor - Background color (default white)
 */
export function createMinimalCanvasConfig(
  width: number,
  height: number,
  backgroundColor = '#FFFFFF'
): CanvasConfig {
  return {
    mockupWidth: width,
    mockupHeight: height,
    printArea: {
      x: 0,
      y: 0,
      width,
      height,
    },
    mockupImageUrl: '',
    backgroundColor,
    isHeathered: false,
  };
}
